import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Camera, Copy, Share2, Award, Plus, X } from 'lucide-react';

export default function Profile() {
  const { user, refreshProfile } = useAuth();
  const [tierData, setTierData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState('');
  const [copied, setCopied] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setBio(user?.bio || '');
    setPhotos(user?.photos || []);
    api.get('/referral/tier').then((r) => setTierData(r.data)).catch(() => {});
    refreshProfile().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPhotos(user?.photos || []);
  }, [user?.photos]);

  const handleSaveBio = async () => {
    try {
      await api.put('/profile/me', { bio });
      await refreshProfile();
      setEditing(false);
    } catch (e) { console.error(e); }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Fichier trop volumineux (max 5 Mo)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/users/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        await refreshProfile();
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors du telechargement');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDeletePhoto = async (index) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.delete(`/users/photo/${index}`);
      await refreshProfile();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const copyCode = () => {
    if (user?.referral_code) {
      const code = user.referral_code;
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }).catch(() => fallbackCopy(code));
      } else {
        fallbackCopy(code);
      }
    }
  };

  const fallbackCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const currentTier = tierData?.tier;
  const nextTier = tierData?.next_tier;

  return (
    <div className="h-full overflow-y-auto" data-testid="profile-page">
      <header className="px-8 py-6" style={{borderBottom:'1px solid var(--border-color)'}}>
        <h1 className="text-2xl font-black" style={{color:'var(--text-primary)'}}>Mon Profil</h1>
      </header>

      <div className="max-w-3xl mx-auto p-8 space-y-6">
        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-muted)'}}>
            <Camera size={16} /> Mes photos ({photos.length}/3)
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {photos.map((photo, i) => (
              <div key={photo} className="relative aspect-square rounded-xl overflow-hidden group" style={{background:'var(--bg-card)'}}>
                <img
                  src={photo.startsWith('data:') || photo.startsWith('http') ? photo : `${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}${photo}`}
                  alt={`Photo ${i + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => handleDeletePhoto(i)}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-photo-${i}`}
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ))}
            {photos.length < 3 && (
              <label className="aspect-square rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-[var(--bg-card-hover)]"
                style={{background:'var(--bg-card)', border:'2px dashed var(--border-color)'}}
                data-testid="upload-photo-btn"
              >
                {uploading ? (
                  <div className="w-6 h-6 border-2 border-brand-violet border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Plus size={24} className="text-brand-violet mb-1" />
                    <span className="text-xs font-medium" style={{color:'var(--text-muted)'}}>Ajouter</span>
                  </>
                )}
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUploadPhoto} className="hidden" />
              </label>
            )}
          </div>
        </div>

        <div className="glass-card p-6 flex items-start gap-6">
          <div className="relative w-20 h-20 rounded-2xl overflow-hidden shrink-0" style={{background:'var(--bg-card)'}}>
            {photos[0] ? (
              <img src={photos[0].startsWith('data:') || photos[0].startsWith('http') ? photos[0] : `${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}${photos[0]}`} alt={user?.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl font-black" style={{color:'var(--text-faint)'}}>{user?.name?.[0]}</div>
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-black" style={{color:'var(--text-primary)'}}>{user?.name}{user?.age ? `, ${user.age}` : ''}</h2>
            <p className="text-sm mt-0.5" style={{color:'var(--text-muted)'}}>{user?.email}</p>
            <div className="flex items-center gap-2 mt-3">
              <span className="px-3 py-1 rounded-full bg-brand-violet/15 text-brand-violet text-xs font-semibold">{user?.credits || 0} credits</span>
              {user?.is_verified && <span className="px-3 py-1 rounded-full bg-green-500/15 text-green-400 text-xs font-semibold">Verifie</span>}
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{color:'var(--text-muted)'}}>A propos de moi</h3>
            <button onClick={() => editing ? handleSaveBio() : setEditing(true)} className="text-xs text-brand-violet font-semibold hover:opacity-70" data-testid="edit-bio-btn">
              {editing ? 'Sauvegarder' : 'Modifier'}
            </button>
          </div>
          {editing ? (
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} className="input-field min-h-[80px] resize-none" placeholder="Parlez de vous..." data-testid="bio-textarea" />
          ) : (
            <p className="text-sm" style={{color:'var(--text-secondary)'}}>{user?.bio || 'Aucune description'}</p>
          )}
        </div>

        <div className="glass-card p-6">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-muted)'}}><Share2 size={16} /> Parrainage</h3>
          <div className="flex items-center gap-3">
            <div className="flex-1 input-field !py-3 text-center font-mono font-bold text-brand-violet tracking-widest">{user?.referral_code || '---'}</div>
            <button onClick={copyCode} className="btn-primary !py-3 !px-6 flex items-center gap-2" data-testid="copy-referral-btn">
              <Copy size={16} /> {copied ? 'Copie !' : 'Copier'}
            </button>
          </div>
          <p className="text-xs mt-3" style={{color:'var(--text-faint)'}}>Partagez ce code pour gagner 20 credits par inscription</p>
        </div>

        {tierData && currentTier && (
          <div className="glass-card p-6">
            <h3 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{color:'var(--text-muted)'}}><Award size={16} /> Niveau de parrainage</h3>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: `${currentTier.color}20` }}>
                {currentTier.icon === 'diamond' ? '\u{1F48E}' : currentTier.icon === 'trophy' ? '\u{1F3C6}' : currentTier.icon === 'shield' ? '\u{1F6E1}\ufe0f' : currentTier.icon === 'star' ? '\u2b50' : currentTier.icon === 'flame' ? '\u{1F525}' : '\u{1F331}'}
              </div>
              <div>
                <p className="font-bold" style={{color:'var(--text-primary)'}}>{currentTier.name}</p>
                <p className="text-sm" style={{color:'var(--text-secondary)'}}>{tierData.total_direct} filleul{tierData.total_direct !== 1 ? 's' : ''}</p>
              </div>
            </div>
            {nextTier && (
              <div>
                <div className="flex justify-between text-xs mb-2" style={{color:'var(--text-muted)'}}>
                  <span>{tierData.total_direct} / {nextTier.min}</span>
                  <span>{nextTier.name}</span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{background:'var(--bg-input)'}}>
                  <div className="h-full rounded-full bg-gradient-to-r from-brand-violet to-brand-pink transition-all" style={{ width: `${Math.min(100, tierData.progress * 100)}%` }} />
                </div>
                <p className="text-xs mt-2" style={{color:'var(--text-faint)'}}>Encore {tierData.remaining_for_next} parrainage{tierData.remaining_for_next > 1 ? 's' : ''} pour {nextTier.name}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
